import type { Harness, ToolPolicy } from "core";
import { httpApiAdapter } from "./httpAdapter";
import type {
	AgentDeployment,
	AgentDetail,
	AgentFileRecord,
	AgentListItem,
	AgentReplay,
	AgentSession,
	AgentTrigger,
	AgentTriggerRun,
	ApiKeyRecord,
	Approval,
	ApprovalDecisionInput,
	ApprovalFilters,
	BadgeStyle,
	ConnectSlackBotInput,
	ConversationDetail,
	ConversationSummary,
	CreatedAgentTrigger,
	CustomProviderApi,
	EgressAllowRules,
	EgressAuth,
	EgressBoundaryStatus,
	EgressConnection,
	EgressContextSpec,
	EgressLogEntry,
	EgressPreset,
	KeyCheckResult,
	McpAuth,
	McpOAuthConnectResult,
	McpProbeResult,
	McpResource,
	McpServer,
	McpServerPreset,
	ModelCatalogResponse,
	PasswordResetResult,
	PromoteDeploymentResult,
	ProvidersResponse,
	SandboxClass,
	SessionEvent,
	SessionSnapshot,
	SlackBotStatus,
	TriggerBadge,
	UsageOverview,
	VerifyEmailResult,
	WorkerStatusView,
	WorkspaceInvitation,
	WorkspaceMember,
	WorkspaceSettings,
	WorkspaceTrigger,
} from "./types";

export type UpsertProviderInput = {
	name: string;
	/** A standard builtin type, or "custom" for a declared endpoint. */
	type: string;
	displayName?: string;
	/** Write-only sealed API key; omit on update to keep the existing one. */
	secret?: string;
	// Declared-endpoint fields, required when type is "custom".
	baseUrl?: string;
	api?: CustomProviderApi;
	auth?: { header: string; format: string };
	models?: Array<{
		id: string;
		contextWindow?: number;
		maxTokens?: number;
		reasoning?: boolean;
	}>;
};

export type CheckProviderKeyInput = Omit<UpsertProviderInput, "name" | "models"> & {
	name?: string;
	models?: UpsertProviderInput["models"];
};

export type ConversationFilters = {
	profile?: string;
	metadataUserId?: string;
	metadataTriggerId?: string;
};

export type ModelCatalogFilters = {
	provider?: string;
	q?: string;
	harness?: Harness;
};

export type UsageQuery = {
	from: string;
	to: string;
	granularity: "hour" | "day" | "total";
	profile?: string;
	apiKeyId?: string;
};

export type CreateAgentInput = {
	name: string;
	slug: string;
	systemPrompt: string;
	model: string;
	thinkingLevel: AgentDetail["thinkingLevel"];
	allowedModels: string[];
	allowedThinkingLevels: AgentDetail["allowedThinkingLevels"];
	tools: string[];
	harness?: AgentDetail["harness"];
	metadata: Record<string, unknown>;
	/** Hosts reachable in full through the egress boundary; omit to leave unchanged. */
	egressAllowedHosts?: string[];
	/** Worker only; omit to leave unchanged, null to reset to the harness default. */
	workload?: AgentDetail["workload"];
	/** Sandbox class id; omit to leave unchanged, null to fall back to host defaults. */
	sandboxClassId?: string | null;
	/** Per-tool policy; omit to leave unchanged, null to allow everything. */
	toolPolicy?: ToolPolicy | null;
};

export type SandboxClassInput = {
	name: string;
	description: string;
	maxCpus: number;
	maxMemMb: number;
	maxDiskMb: number;
	maxTimeSec: number;
	/** Hidden boundary defaults; the server fills them when omitted. */
	maxPids?: number;
	maxFsizeMb?: number;
};

export type UpdateSandboxClassInput = Partial<SandboxClassInput>;

export type UpdateAgentInput = CreateAgentInput;

export type CreateApiKeyInput = {
	name: string;
	scopes: string[];
	capabilities?: string[];
	requestsPerMinute: number;
	maxConcurrent: number;
	monthlyTokenBudget?: number;
	expiresAt?: string;
};

export type UpdateApiKeyInput = {
	name?: string;
	scopes?: string[];
	capabilities?: string[];
	/** Replaces the key's limits as a whole; 0 (or no budget) means unlimited. */
	rateLimits?: ApiKeyRecord["rateLimits"];
	/** A `YYYY-MM-DD` date (the key dies at the end of it), or null to never expire. */
	expiresAt?: string | null;
};

export type CreateTriggerInput = {
	name: string;
	message: string;
};

export type UpdateTriggerInput = {
	name?: string;
	message?: string;
	enabled?: boolean;
	onFailureTriggerId?: string | null;
};

export type CreateReplayInput = {
	conversationIds: string[];
	deploymentId?: string;
};

export type MintBadgeInput = {
	label: string;
	style?: BadgeStyle;
	rateLimitPerHour?: number;
	showReply?: boolean;
	allowInput?: boolean;
};

export type UpdateBadgeInput = {
	label?: string;
	style?: BadgeStyle;
	rateLimitPerHour?: number;
	showReply?: boolean;
	allowInput?: boolean;
};

export type TriggerRunFilters = {
	triggerId?: string;
	limit?: number;
};

export type InviteMemberInput = {
	email: string;
	role: WorkspaceMember["role"];
};

/** Only the fields that changed. `rateLimits` is stored as one object, so it travels whole. */
export type UpdateWorkspaceInput = {
	name?: string;
	rateLimits?: WorkspaceSettings["rateLimits"];
};

export type UpsertEgressConnectionInput = {
	name: string;
	targetUrl: string;
	auth: EgressAuth;
	/** Boundary-proxy restrictions; omit for all methods, target-derived paths. */
	allow?: EgressAllowRules;
	/** Required when targetUrl templates its host with {ctx.*}. */
	hostPatterns?: string[];
	/** Which request-context keys this connection consumes, and which are secret. */
	context?: EgressContextSpec;
	/** URL-only withholds the scoped placeholder token from the sandbox. */
	expose?: "url" | "url-and-token";
	/** Opt in to buffering and substituting supported request bodies. */
	bodySubstitution?: boolean;
	/** Optional ASCII token prefix ending in the literal `{placeholder}`. */
	placeholderTemplate?: string | null;
	/** Write-only; omit on update to keep the existing sealed secret. */
	secret?: string;
	/** Env secret key to delete in the same transaction (convert-to-connection). */
	deleteSecretKey?: string;
};

export type McpProbeInput =
	| {
			transport: "http";
			name: string;
			url: string;
			auth: McpAuth | { type: "bearer" };
			secret?: string;
			drift: "strict" | "live";
			required: boolean;
			callTimeoutMs: number;
	  }
	| {
			transport: "http";
			name: string;
			connection: string;
			path: string;
			drift: "strict" | "live";
			required: boolean;
			callTimeoutMs: number;
	  }
	| {
			transport: "stdio";
			name: string;
			command?: string;
			package?: string;
			args: string[];
			cwd: string;
			env: Record<string, string>;
			startupTimeoutMs: number;
			drift: "strict" | "live";
			required: boolean;
			callTimeoutMs: number;
	  };

type McpServerReviewInput = {
	name: string;
	tools: string[];
	toolSnapshot: McpServer["toolSnapshot"];
	resources: boolean;
	resourceSnapshot: McpResource[];
	snapshotDigest: string;
	drift: "strict" | "live";
	connect: "eager" | "lazy";
	required: boolean;
	callTimeoutMs: number;
};

export type UpsertMcpServerInput = McpServerReviewInput &
	(
		| ({ transport: "http" } & (
				| { url: string; auth: McpAuth; secret?: string }
				| { connection: string; path: string }
		  ))
		| {
				transport: "stdio";
				command?: string;
				package?: string;
				args: string[];
				cwd: string;
				env: Record<string, string>;
				startupTimeoutMs: number;
		  }
	);

export interface AasApiAdapter {
	version(): Promise<{ version: string }>;
	auth: {
		requestPasswordReset(email: string): Promise<PasswordResetResult>;
		resetPassword(token: string, newPassword: string): Promise<PasswordResetResult>;
		verifyEmail(token: string): Promise<VerifyEmailResult>;
	};
	agents: {
		list(): Promise<AgentListItem[]>;
		get(slug: string): Promise<AgentDetail | null>;
		create(input: CreateAgentInput): Promise<AgentDetail>;
		update(slug: string, input: UpdateAgentInput): Promise<AgentDetail>;
		delete(slug: string): Promise<void>;
		/** Live supervisor state; 404s for non-worker agents. */
		workerStatus(slug: string): Promise<WorkerStatusView>;
		/** Clear the crash breaker and restart the worker's process. */
		resetWorker(slug: string): Promise<void>;
		/** Create or replace an environment variable; values are write-only. */
		setSecret(slug: string, key: string, value: string): Promise<void>;
		deleteSecret(slug: string, key: string): Promise<void>;
	};
	approvals: {
		list(filters?: ApprovalFilters): Promise<Approval[]>;
		get(id: string): Promise<Approval | null>;
		pendingCount(): Promise<number>;
		resolve(id: string, input: ApprovalDecisionInput): Promise<Approval>;
	};
	agentFiles: {
		list(slug: string): Promise<AgentFileRecord[]>;
		getContent(slug: string, path: string): Promise<string>;
		upload(slug: string, files: Array<{ path: string; content: string }>): Promise<void>;
		delete(slug: string, path: string): Promise<void>;
	};
	sandboxClasses: {
		list(): Promise<SandboxClass[]>;
		get(id: string): Promise<SandboxClass | null>;
		create(input: SandboxClassInput): Promise<SandboxClass>;
		update(id: string, input: UpdateSandboxClassInput): Promise<SandboxClass>;
		remove(id: string): Promise<void>;
	};
	deployments: {
		list(slug: string): Promise<AgentDeployment[]>;
		promote(slug: string, id: string): Promise<PromoteDeploymentResult>;
	};
	triggers: {
		list(slug: string): Promise<AgentTrigger[]>;
		listAll(): Promise<WorkspaceTrigger[]>;
		runs(slug: string, filters?: TriggerRunFilters): Promise<AgentTriggerRun[]>;
		create(slug: string, input: CreateTriggerInput): Promise<CreatedAgentTrigger>;
		update(slug: string, id: string, input: UpdateTriggerInput): Promise<AgentTrigger>;
		delete(slug: string, id: string): Promise<void>;
		badge(slug: string, triggerId: string): Promise<TriggerBadge | null>;
		mintBadge(slug: string, triggerId: string, input: MintBadgeInput): Promise<TriggerBadge>;
		updateBadge(slug: string, triggerId: string, input: UpdateBadgeInput): Promise<TriggerBadge>;
		revokeBadge(slug: string, triggerId: string): Promise<void>;
	};
	slackBot: {
		get(slug: string): Promise<SlackBotStatus>;
		/** Checks the token with Slack, then seals both values; a second call replaces them. */
		connect(slug: string, input: ConnectSlackBotInput): Promise<SlackBotStatus>;
		setEnabled(slug: string, enabled: boolean): Promise<SlackBotStatus>;
		disconnect(slug: string): Promise<void>;
	};
	replays: {
		list(slug: string): Promise<AgentReplay[]>;
		create(slug: string, input: CreateReplayInput): Promise<void>;
	};
	models: {
		list(filters?: ModelCatalogFilters): Promise<ModelCatalogResponse>;
	};
	conversations: {
		list(filters: ConversationFilters): Promise<ConversationSummary[]>;
		get(id: string): Promise<ConversationDetail | null>;
	};
	sessions: {
		list(): Promise<AgentSession[]>;
		create(input: {
			profileSlug: string;
			conversationId?: string;
			runtime?: {
				model?: string;
				thinkingLevel?: AgentDetail["thinkingLevel"];
			};
			metadata?: Record<string, unknown>;
			files?: Array<{ path: string; content: string }>;
		}): Promise<AgentSession>;
		get(id: string): Promise<AgentSession | null>;
		submitTurn(
			id: string,
			input: {
				message: string;
				env?: Record<string, string>;
				files?: Array<{ path: string; content: string }>;
			},
		): Promise<void>;
		command(id: string, command: Record<string, unknown>): Promise<void>;
		snapshot(id: string): Promise<SessionSnapshot | null>;
		subscribe(
			id: string,
			onEvent: (event: SessionEvent) => void,
			fromSeq?: number,
		): Promise<() => void>;
	};
	egress: {
		list(slug: string): Promise<EgressConnection[]>;
		upsert(slug: string, input: UpsertEgressConnectionInput): Promise<void>;
		delete(slug: string, name: string): Promise<void>;
		log(slug: string): Promise<EgressLogEntry[]>;
		boundaryStatus(): Promise<EgressBoundaryStatus>;
		presets(): Promise<EgressPreset[]>;
	};
	mcp: {
		list(slug: string): Promise<McpServer[]>;
		presets(): Promise<McpServerPreset[]>;
		connectOAuth(
			slug: string,
			name: string,
			input: { url?: string; clientId?: string; clientSecret?: string; scopes?: string[] },
		): Promise<McpOAuthConnectResult>;
		disconnectOAuth(
			slug: string,
			name: string,
		): Promise<{ disconnected: true; providerRevoked: boolean }>;
		probe(slug: string, input: McpProbeInput): Promise<McpProbeResult>;
		upsert(slug: string, input: UpsertMcpServerInput): Promise<void>;
		delete(slug: string, name: string): Promise<void>;
	};
	providers: {
		list(): Promise<ProvidersResponse>;
		upsert(input: UpsertProviderInput): Promise<void>;
		delete(name: string): Promise<void>;
		/** Validate a key against the provider without saving anything. */
		check(input: CheckProviderKeyInput): Promise<KeyCheckResult>;
	};
	apiKeys: {
		list(): Promise<ApiKeyRecord[]>;
		create(input: CreateApiKeyInput): Promise<{ keyId: string; fullKey: string }>;
		update(id: string, input: UpdateApiKeyInput): Promise<ApiKeyRecord>;
		revoke(id: string): Promise<ApiKeyRecord>;
		delete(id: string): Promise<void>;
	};
	usage: {
		overview(query: UsageQuery): Promise<UsageOverview>;
		byProfile(slug: string, query: UsageQuery): Promise<UsageOverview>;
		/** `keyName` when the key is listed; `budget`: tokens used this UTC month against its monthly token budget. */
		byApiKey(
			id: string,
			query: UsageQuery,
		): Promise<UsageOverview & { keyName?: string; budget?: { used: number; limit: number } }>;
	};
	settings: {
		workspace(): Promise<WorkspaceSettings>;
		members(): Promise<WorkspaceMember[]>;
		invitations(): Promise<WorkspaceInvitation[]>;
		invite(input: InviteMemberInput): Promise<WorkspaceInvitation>;
		updateWorkspace(input: UpdateWorkspaceInput): Promise<WorkspaceSettings>;
		updateMemberRole(memberId: string, role: WorkspaceMember["role"]): Promise<void>;
		removeMember(memberId: string): Promise<void>;
		cancelInvitation(invitationId: string): Promise<void>;
	};
}

export const apiAdapter: AasApiAdapter = httpApiAdapter;
